/**
 * Hook for unified save logic with versioning support
 *
 * Provides intelligent save behavior based on transaction status:
 * - Draft status: Saves directly without prompting
 * - Reconciling/Approved/Finalized: Prompts to save to draft OR create new version
 */

import { useCallback, useState } from 'react';
import type { TransactionStatus } from '../types';
import { useCreateVersion } from './useVersions';

export type SaveAction = 'save_draft' | 'create_version';

export interface SaveWithVersioningOptions {
  transactionId: string;
  transactionStatus: TransactionStatus;
  onSaveDraft: () => Promise<void>;
  onVersionCreated?: (versionNumber: number) => void;
}

export interface SaveWithVersioningResult {
  /**
   * Whether the save options dialog should be shown
   */
  shouldPrompt: boolean;

  /**
   * Whether a save operation is in progress
   */
  isSaving: boolean;

  /**
   * Error from last save attempt
   */
  error: Error | null;

  /**
   * Show the save options dialog
   */
  showSaveDialog: boolean;

  /**
   * Open the save dialog (only when shouldPrompt is true)
   */
  openSaveDialog: () => void;

  /**
   * Close the save dialog without saving
   */
  closeSaveDialog: () => void;

  /**
   * Execute save action - either direct save or version creation
   */
  executeSave: (action: SaveAction, notes?: string) => Promise<void>;

  /**
   * Save directly without prompting (for draft status)
   */
  saveDirectly: () => Promise<void>;
}

/**
 * Statuses that require prompting before save
 */
const PROMPT_STATUSES: TransactionStatus[] = ['reconciling', 'approved', 'finalized'];

/**
 * Hook for save with versioning support
 */
export function useSaveWithVersioning(
  options: SaveWithVersioningOptions,
): SaveWithVersioningResult {
  const { transactionId, transactionStatus, onSaveDraft, onVersionCreated } = options;

  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createVersionMutation = useCreateVersion();

  // Determine if we should prompt based on transaction status
  const shouldPrompt = PROMPT_STATUSES.includes(transactionStatus);

  const openSaveDialog = useCallback(() => {
    if (shouldPrompt) {
      setShowSaveDialog(true);
      setError(null);
    }
  }, [shouldPrompt]);

  const closeSaveDialog = useCallback(() => {
    setShowSaveDialog(false);
  }, []);

  const saveDirectly = useCallback(async () => {
    setIsSaving(true);
    setError(null);
    try {
      await onSaveDraft();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to save'));
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [onSaveDraft]);

  const executeSave = useCallback(
    async (action: SaveAction, notes?: string) => {
      setIsSaving(true);
      setError(null);

      try {
        if (action === 'save_draft') {
          // Just save to current draft
          await onSaveDraft();
        } else if (action === 'create_version') {
          // First save the current state, then create a version
          await onSaveDraft();

          const result = await createVersionMutation.mutateAsync({
            transactionId,
            request: { notes },
          });

          if (onVersionCreated) {
            onVersionCreated(result.version.versionNumber);
          }
        }

        setShowSaveDialog(false);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to save');
        setError(error);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [transactionId, onSaveDraft, createVersionMutation, onVersionCreated],
  );

  return {
    shouldPrompt,
    isSaving,
    error,
    showSaveDialog,
    openSaveDialog,
    closeSaveDialog,
    executeSave,
    saveDirectly,
  };
}

/**
 * Get version type label based on transaction status
 */
export function getVersionTypeLabel(status: TransactionStatus): string {
  switch (status) {
    case 'reconciling':
      return 'Reconciled';
    case 'approved':
      return 'Approved';
    case 'finalized':
      return 'Final';
    default:
      return 'Draft';
  }
}
